import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { kanbanService } from '@/features/kanban/services/kanban.service';
import { tasksBoardService } from '@/features/kanban/services/tasks-board.service';
import { useTasksBoardByEmail } from '@/features/kanban/hooks/useTasksBoardByEmail';
import { dailyTasksService } from '@/features/daily-tasks/services/daily-tasks.service';
import { usersService } from '@/features/users/services/users.service';
import { BoardFilterModal } from '@/features/dashboard/components/BoardFilterModal';
import { NotionQueueColumn } from '@/features/dashboard/components/NotionQueueColumn';
import { PomodoroColumn } from '@/features/dashboard/components/PomodoroColumn';
import { CompletedTasksColumn } from '@/features/dashboard/components/CompletedTasksColumn';
import { CalendarColumn } from '@/features/dashboard/components/CalendarColumn';
import { UpcomingDeadlinesColumn } from '@/features/dashboard/components/UpcomingDeadlinesColumn';
import { RecurringClientTasksColumn } from '@/features/dashboard/components/RecurringClientTasksColumn';
import { TextQueuesView } from '@/features/dashboard/components/TextQueuesView';
import { Button } from '@/components/ui/button';
import { StickyHorizontalScrollbar } from '@/components/ui/sticky-horizontal-scrollbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Loader2, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PermissionGate } from '@/components/PermissionGate';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { sortNotionTasksByDueDate } from '@/features/kanban/utils/sortNotionTasksByDueDate';

export const Route = createFileRoute('/_authenticated/task-management')({
  component: TaskManagementPage,
});

function TaskManagementPage() {
  return (
    <PermissionGate permission={Permission.NOTION_MANAGE_TASKS}>
      <TaskManagementPageContent />
    </PermissionGate>
  );
}

function TaskManagementPageContent() {
  const { hasPermission } = usePermissions();
  const canSeeRecurringTasks = hasPermission(Permission.RECURRING_CLIENT_TASKS_VIEW);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Buscar todas as colunas para obter lista de emails
  const { data: allTasksBoardData } = useQuery({
    queryKey: ['tasks-board'],
    queryFn: tasksBoardService.getTasksBoard,
  });

  // Extrair lista de emails únicos das colunas
  const availableEmails = useMemo(() => {
    const columns = (allTasksBoardData as any)?.columns || [];
    const emailSet = new Set<string>();
    columns.forEach((col: any) => {
      if (col.email && col.email !== 'contato@digitalfluxo.com') {
        emailSet.add(col.email);
      }
    });
    return Array.from(emailSet).sort();
  }, [allTasksBoardData]);

  const { data: tenantUsers, isSuccess: tenantUsersLoaded } = useQuery({
    queryKey: ['tenant-users-task-management'],
    queryFn: async () => {
      const res = await usersService.getAll();
      return res.data;
    },
  });

  const registeredEmailsLower = useMemo(() => {
    const set = new Set<string>();
    for (const u of tenantUsers ?? []) {
      if (u.email) set.add(u.email.toLowerCase().trim());
    }
    return set;
  }, [tenantUsers]);

  const usersDirectoryLoaded = tenantUsersLoaded;
  const selectedEmailNormalized = selectedEmail ? selectedEmail.toLowerCase().trim() : '';
  const showUnregisteredUserHint =
    !!selectedEmail &&
    usersDirectoryLoaded &&
    !registeredEmailsLower.has(selectedEmailNormalized);

  const emailRegistrationSuffix = (email: string) =>
    usersDirectoryLoaded && !registeredEmailsLower.has(email.toLowerCase().trim())
      ? ' — sem usuário no Asseto'
      : '';

  // Selecionar primeiro email por padrão quando a lista carregar
  useEffect(() => {
    if (availableEmails.length > 0 && !selectedEmail) {
      setSelectedEmail(availableEmails[0]);
    }
  }, [availableEmails, selectedEmail]);

  // Buscar boards
  const { data: boards = [] } = useQuery({
    queryKey: ['notion-boards'],
    queryFn: () => kanbanService.getMyBoards(),
  });

  // Buscar coluna do email selecionado
  const {
    data: tasksBoardData,
    isLoading: loadingNotion,
    refetch,
    isSocketConnected,
    lastUpdate,
  } = useTasksBoardByEmail({ email: selectedEmail, enabled: !!selectedEmail });

  // Verificar se há colunas de responsabilidade para mostrar a tab
  const { data: responsibilityData } = useQuery({
    queryKey: ['tasks-board-by-responsibility-email', selectedEmail],
    queryFn: () => tasksBoardService.getTasksBoardByResponsibilityParam(selectedEmail),
    enabled: !!selectedEmail,
  });

  const hasResponsibilityQueues =
    (responsibilityData as any)?.columns && (responsibilityData as any).columns.length > 0;

  // Obter a coluna retornada (deve ter apenas uma coluna com o email selecionado)
  const userColumn = useMemo(() => {
    const columns = (tasksBoardData as any)?.columns || [];
    // Deve retornar apenas uma coluna, mas pegamos a primeira se houver múltiplas
    return columns.length > 0 ? columns[0] : null;
  }, [tasksBoardData]);

  // Filtrar tarefas baseado nos boards selecionados
  const filteredTasks = useMemo(() => {
    if (!userColumn || !userColumn.tasks) return [];

    const allTasks = userColumn.tasks;

    // Se nenhum board selecionado, mostrar todas as tarefas
    if (selectedBoardIds.length === 0) {
      return sortNotionTasksByDueDate<any>(allTasks);
    }

    // Filtrar tarefas pelos boards selecionados
    const tasksByBoards = allTasks.filter((task: any) => {
      const taskBoardId = task.boardId;
      return taskBoardId && selectedBoardIds.includes(taskBoardId);
    });
    return sortNotionTasksByDueDate<any>(tasksByBoards);
  }, [userColumn, selectedBoardIds]);

  // Função para verificar se a tarefa está aguardando aprovação
  const isWaitingApproval = (task: any) => {
    return (
      task.textCheckbox === true &&
      task.artCheckbox === true &&
      task.clientApprovalCheckbox === false
    );
  };

  // Separar tarefas: "Para executar" e "Aguardando Aprovação"
  // Regra: tudo que aparece no quadro de tarefas (para o usuário selecionado) deve aparecer em "Para executar",
  // exceto o que está aguardando aprovação do cliente.
  const tasksToExecute = useMemo(
    () => filteredTasks.filter((task: any) => !isWaitingApproval(task)),
    [filteredTasks],
  );
  const tasksWaitingApproval = useMemo(
    () => filteredTasks.filter((task: any) => isWaitingApproval(task)),
    [filteredTasks],
  );

  // Buscar tarefas diárias do usuário selecionado
  const { data: dailyTasks = [], isLoading: loadingDaily } = useQuery({
    queryKey: ['daily-tasks-by-email', selectedEmail],
    queryFn: () => dailyTasksService.getTasksByEmail(selectedEmail),
    enabled: !!selectedEmail,
  });

  // Calendário da equipe fica na coluna Calendário, não no Pomodoro (igual ao my-dashboard)
  const dailyTasksWithoutTeamCalendar = useMemo(
    () => dailyTasks.filter((t) => t.isTeamCalendar !== true),
    [dailyTasks],
  );

  // Separar tarefas ativas e concluídas
  const activeDailyTasks = useMemo(
    () => dailyTasksWithoutTeamCalendar.filter((t) => t.status !== 'done'),
    [dailyTasksWithoutTeamCalendar],
  );
  const completedDailyTasks = useMemo(
    () =>
      dailyTasks
        .filter((t) => t.status === 'done')
        .sort((a, b) => {
          if (!a.completedAt || !b.completedAt) return 0;
          return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
        }),
    [dailyTasks],
  );

  const lastUpdateText = useMemo(() => {
    if (!lastUpdate) return 'Nunca atualizado';
    try {
      return formatDistanceToNow(new Date(lastUpdate), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return 'Agora';
    }
  }, [lastUpdate]);

  // Handlers para drag-to-scroll
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    
    // Verificar se o clique foi em um elemento interativo
    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('select') ||
      target.closest('[role="button"]') ||
      target.closest('[draggable="true"]') ||
      target.closest('.task-item') ||
      target.closest('[data-draggable]');
    
    if (isInteractiveElement) return;
    
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
    scrollContainerRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
      scrollContainerRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
      scrollContainerRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleBoardFilterChange = (boardIds: string[]) => {
    setSelectedBoardIds(boardIds);
  };

  // Quando os boards carregarem pela primeira vez, selecionar todos por padrão
  useEffect(() => {
    if (boards.length > 0 && selectedBoardIds.length === 0) {
      setSelectedBoardIds(boards.map((b: any) => b.id));
    }
  }, [boards, selectedBoardIds.length]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Tarefas</h1>
          <p className="text-muted-foreground">Visualize e gerencie tarefas por usuário</p>
        </div>
        <div className="flex items-center gap-4">
          {selectedEmail && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isSocketConnected ? (
                <Wifi className="h-4 w-4 text-green-500" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-500" />
              )}
              <span>
                {isSocketConnected ? 'Conectado' : 'Desconectado'}
                {lastUpdate && ` • Última atualização: ${lastUpdateText}`}
                {!isSocketConnected && ' • Atualizando a cada 30s'}
              </span>
            </div>
          )}
          <Button
            onClick={() => refetch()}
            disabled={loadingNotion || !selectedEmail}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingNotion ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {!selectedEmail ? (
        <div className="space-y-2">
          <Label htmlFor="email-selector">Selecionar Usuário</Label>
          <Select value={selectedEmail} onValueChange={setSelectedEmail}>
            <SelectTrigger id="email-selector" className="w-full md:w-[400px]">
              <SelectValue placeholder="Selecione um email" />
            </SelectTrigger>
            <SelectContent>
              {availableEmails.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  Carregando emails...
                </div>
              ) : (
                availableEmails.map((email) => {
                  const column = (allTasksBoardData as any)?.columns?.find(
                    (col: any) => col.email === email,
                  );
                  const name = column?.name || email;
                  return (
                    <SelectItem key={email} value={email}>
                      {name} ({email}){emailRegistrationSuffix(email)}
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-end gap-4 flex-wrap mb-6">
            <div className="space-y-2">
              <Label htmlFor="email-selector">Selecionar Usuário</Label>
              <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                <SelectTrigger id="email-selector" className="w-[300px]">
                  <SelectValue placeholder="Selecione um email" />
                </SelectTrigger>
                <SelectContent>
                  {availableEmails.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Carregando emails...
                    </div>
                  ) : (
                    availableEmails.map((email) => {
                      const column = (allTasksBoardData as any)?.columns?.find(
                        (col: any) => col.email === email,
                      );
                      const name = column?.name || email;
                      return (
                        <SelectItem key={email} value={email}>
                          {name} ({email}){emailRegistrationSuffix(email)}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            <TabsList>
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
              {hasResponsibilityQueues && (
                <TabsTrigger value="responsibility">Filas de tarefas que gerencia</TabsTrigger>
              )}
            </TabsList>
            {activeTab === 'dashboard' && (
              <BoardFilterModal
                boards={boards}
                selectedBoardIds={
                  selectedBoardIds.length > 0 ? selectedBoardIds : boards.map((b: any) => b.id)
                }
                onChange={handleBoardFilterChange}
              />
            )}
            {activeTab === 'responsibility' && hasResponsibilityQueues && (
              <BoardFilterModal
                boards={boards}
                selectedBoardIds={
                  selectedBoardIds.length > 0 ? selectedBoardIds : boards.map((b: any) => b.id)
                }
                onChange={handleBoardFilterChange}
              />
            )}
          </div>

          {showUnregisteredUserHint && (
            <Alert className="mb-4 border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/25">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertTitle className="text-amber-900 dark:text-amber-100">
                E-mail do Notion sem cadastro no Asseto
              </AlertTitle>
              <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
                A coluna usa <span className="font-medium">{selectedEmail}</span>, mas não há usuário
                neste tenant com esse e-mail. Por isso as chamadas a Pomodoro, tarefas diárias e
                calendário pessoal retornam vazio (não é falha de carregamento). Cadastre o usuário
                com o mesmo e-mail ou ajuste o responsável no Notion.
              </AlertDescription>
            </Alert>
          )}

          <TabsContent value="dashboard" className="space-y-6">
            {loadingNotion ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div>
                <div
                  ref={scrollContainerRef}
                  className="overflow-x-auto cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeave}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                >
                  <div className="flex gap-6 min-w-max pb-4">
                    <NotionQueueColumn
                      title="Para executar"
                      tasks={tasksToExecute}
                      isLoading={loadingNotion}
                      dailyTasks={dailyTasks}
                      onTaskDrag={() => {
                        // Handler vazio - o drag and drop é gerenciado pela coluna Pomodoro
                      }}
                    />
                    {tasksWaitingApproval.length > 0 && (
                      <NotionQueueColumn
                        title="Aguardando Aprovação"
                        tasks={tasksWaitingApproval}
                        isLoading={loadingNotion}
                        dailyTasks={dailyTasks}
                        onTaskDrag={() => {
                          // Handler vazio - o drag and drop é gerenciado pela coluna Pomodoro
                        }}
                      />
                    )}
                    <PomodoroColumn
                      tasks={activeDailyTasks}
                      isLoading={loadingDaily}
                      dueDateFilter="all"
                    />
                    <CalendarColumn userEmail={selectedEmail} includeTeamTasks={false} />
                    <UpcomingDeadlinesColumn />
                    {canSeeRecurringTasks && (
                      <RecurringClientTasksColumn selectedUserEmail={selectedEmail} />
                    )}
                    <CompletedTasksColumn tasks={completedDailyTasks} isLoading={loadingDaily} />
                  </div>
                </div>
                <StickyHorizontalScrollbar targetRef={scrollContainerRef} />
              </div>
            )}
          </TabsContent>

          {hasResponsibilityQueues && (
            <TabsContent value="responsibility">
              <TextQueuesView
                selectedEmail={selectedEmail}
                selectedBoardIds={selectedBoardIds}
                onBoardFilterChange={handleBoardFilterChange}
                showBoardFilter={false}
              />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
